import React, { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Box, TextField, Button } from "@mui/material";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import _ from "lodash";

import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface MapPickerProps {
  setPersonalDetailState: (data: any) => void;
  lat?: number;
  lng?: number;
  isEditing?: boolean;
}

const defaultCenter: [number, number] = [28.6139, 77.209];

/** Clear leftover Leaflet state on a DOM node (fixes "already initialized") */
const resetLeafletContainer = (el: HTMLElement | null) => {
  if (!el) return;
  try {
    if ((el as any)._leaflet_id) {
      const id = (el as any)._leaflet_id;
      const map = (L as any).Map?._instances?.[id];
      if (map?.remove) map.remove();
      delete (el as any)._leaflet_id;
    }
  } catch {
    delete (el as any)._leaflet_id;
  }
  el.innerHTML = "";
};

const MapPicker: React.FC<MapPickerProps> = ({
  setPersonalDetailState,
  lat,
  lng,
  isEditing = false,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const isEditingRef = useRef(isEditing);
  const setStateRef = useRef(setPersonalDetailState);
  const [inputAddress, setInputAddress] = useState("");

  isEditingRef.current = isEditing;
  setStateRef.current = setPersonalDetailState;

  const reverseGeocode = useCallback(async (latVal: number, lngVal: number) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latVal}&lon=${lngVal}`,
        { headers: { "User-Agent": "yhataw-crm" } }
      );
      const data = await res.json();
      const zipcode = data?.address?.postcode || "";
      const payload: any = { lat: latVal, lng: lngVal };
      if (!isEditingRef.current) payload.zipcode = zipcode;
      setStateRef.current(payload);
    } catch (err) {
      console.error("Reverse geocode error", err);
    }
  }, []);

  const reverseGeocodeRef = useRef(reverseGeocode);
  reverseGeocodeRef.current = reverseGeocode;

  // Create map once; destroy on unmount
  useEffect(() => {
    const el = containerRef.current;
    if (!el || mapRef.current) return;

    resetLeafletContainer(el);

    const start: [number, number] =
      lat && lng && Number.isFinite(lat) && Number.isFinite(lng)
        ? [lat, lng]
        : defaultCenter;

    const map = L.map(el, {
      zoomControl: false,
      attributionControl: true,
    }).setView(start, 14);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap",
    }).addTo(map);

    L.control.zoom({ position: "bottomright" }).addTo(map);

    const onMoveEnd = _.debounce(() => {
      const c = map.getCenter();
      reverseGeocodeRef.current(c.lat, c.lng);
    }, 800);

    map.on("moveend", onMoveEnd);
    mapRef.current = map;

    // Leaflet needs a tick after layout to size correctly
    requestAnimationFrame(() => {
      map.invalidateSize();
    });

    return () => {
      onMoveEnd.cancel?.();
      try {
        map.off("moveend", onMoveEnd);
        map.remove();
      } catch {
        // ignore
      }
      mapRef.current = null;
      resetLeafletContainer(el);
    };
    // Intentionally mount once — center updates handled below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pan when lat/lng props change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (lat && lng && Number.isFinite(lat) && Number.isFinite(lng)) {
      const cur = map.getCenter();
      if (
        Math.abs(cur.lat - lat) > 0.00005 ||
        Math.abs(cur.lng - lng) > 0.00005
      ) {
        map.setView([lat, lng], map.getZoom() || 14);
      }
    }
  }, [lat, lng]);

  const geocodeAddress = async () => {
    if (!inputAddress.trim()) return;
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          inputAddress
        )}`,
        { headers: { "User-Agent": "yhataw-crm" } }
      );
      const data = await res.json();
      if (data?.length > 0) {
        const nextLat = parseFloat(data[0].lat);
        const nextLng = parseFloat(data[0].lon);
        mapRef.current?.setView([nextLat, nextLng], 14);
        reverseGeocode(nextLat, nextLng);
      } else {
        alert("Location not found");
      }
    } catch (err) {
      console.error("Geocode error", err);
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <Box
        sx={{
          position: "absolute",
          top: 10,
          left: 10,
          right: 10,
          zIndex: 1000,
          display: "flex",
          gap: 1,
          background: "#fff",
          padding: "8px",
          borderRadius: "8px",
        }}
      >
        <TextField
          value={inputAddress}
          onChange={(e) => setInputAddress(e.target.value)}
          placeholder="Search location"
          size="small"
          fullWidth
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              geocodeAddress();
            }
          }}
        />
        <Button
          onClick={geocodeAddress}
          variant="contained"
          startIcon={<PlaceOutlinedIcon />}
        >
          Locate
        </Button>
      </Box>

      <div
        ref={containerRef}
        style={{ height: 400, width: "100%", zIndex: 0 }}
      />

      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -100%)",
          zIndex: 500,
          pointerEvents: "none",
        }}
      >
        <img
          src="https://maps.gstatic.com/mapfiles/api-3/images/spotlight-poi2.png"
          alt="pin"
          style={{ height: "40px" }}
        />
      </div>
    </div>
  );
};

export default MapPicker;
